package com.arena_web.ui.components.overlay

import androidx.compose.runtime.Composable
import com.arena_web.data.dto.FollowUserDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.theme.LocalSportColors
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.dom.Div

private const val DIALOG_BG = "var(--l-s2, #09142d)"
private const val DIVIDER = "0.5px solid var(--l-hairline, rgba(255, 255, 255, 0.06))"

/** Same mask-composite gradient-border helper as in FollowListDialog. */
@Composable
private fun GradientBorder(radiusPx: Int, gradient: String = AppGradients.ButtonBorderGradientPrimary) {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .styleModifier {
                property("position", "absolute")
                property("inset", "0")
                property("border", "1px solid transparent")
                property("border-radius", s(radiusPx))
                property("background", "$gradient border-box")
                property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                property("-webkit-mask-composite", "xor")
                property("mask-composite", "exclude")
                property("pointer-events", "none")
            }
    )
}

/**
 * Small confirmation dialog: avatar + heading + (optional) subtitle + Cancel / Confirm.
 * Used for both "Unfollow @username" and "Remove follower?" flows.
 */
@Composable
fun FollowConfirmDialog(
    user: FollowUserDto,
    title: String,
    subtitle: String? = null,
    confirmLabel: String,
    onConfirm: () -> Unit,
    onCancel: () -> Unit
) {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .styleModifier {
                property("position", "fixed")
                property("top", "0")
                property("left", "0")
                property("z-index", "1100")
                property("background", "rgba(0, 0, 0, 0.6)")
            }
            .onClick { onCancel() },
        contentAlignment = Alignment.Center
    ) {
        Box(
            modifier = Modifier
                .sWidth(331)
                .styleModifier {
                    property("position", "relative")
                    property("border-radius", s(18))
                }
                .onClick { it.stopPropagation() }
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("background", DIALOG_BG)
                        property("border-radius", s(18))
                        property("padding", "${s(16)} ${s(22)} ${s(22)} ${s(22)}")
                        property("gap", s(22))
                        property("align-items", "center")
                    }
            ) {
                Column(
                    modifier = Modifier.fillMaxWidth().sGap(12),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    ConfirmAvatar(user)
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .sPaddingBottom(12)
                            .sGap(8)
                            .styleModifier {
                                property("border-bottom", DIVIDER)
                                property("align-items", "center")
                            },
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        SpanText(
                            title,
                            modifier = Modifier
                                .sFontSize(18)
                                .fontWeight(AppTypography.Medium)
                                .sLineHeight(22)
                                .sLetterSpacing(0.09)
                                .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        )
                        if (subtitle != null) {
                            SpanText(
                                subtitle,
                                modifier = Modifier
                                    .sFontSize(14)
                                    .fontWeight(AppTypography.Regular)
                                    .sLineHeight(22)
                                    .sLetterSpacing(0.028)
                                    .color(com.arena_web.ui.theme.ThemeVars.Text3)
                            )
                        }
                    }
                }

                Row(
                    modifier = Modifier.fillMaxWidth().sGap(12),
                    horizontalArrangement = Arrangement.Center,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    PillButton(label = LocalAppStrings.current.commonCancel, filled = false, onClick = onCancel)
                    PillButton(label = confirmLabel, filled = true, onClick = onConfirm)
                }
            }
        }
    }
}

@Composable
private fun ConfirmAvatar(user: FollowUserDto) {
    val cardFrameUrl = user.cardImageUrl
    Box(
        modifier = Modifier
            .styleModifier {
                property("flex-shrink", "0")
                property("width", s(40))
                property("height", s(60))
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
            },
        contentAlignment = Alignment.Center
    ) {
        if (cardFrameUrl != null) {
            PlayerCard(
                playerName = "",
                rating = 0,
                position = "",
                sport = "football",
                size = PlayerCardSize.Highlight,
                cardFrameUrl = cardFrameUrl,
                avatarUrl = user.cardAvatarUrl ?: AppIcons.EmptyAvatar,
                hideRating = true,
                hideShadow = true,
            )
        } else {
            val avatarUrl = user.avatarUrl
            Box(
                modifier = Modifier
                    .sWidth(40)
                    .sHeight(60)
                    .styleModifier {
                        property("border-radius", s(6))
                        property("background", "rgba(60, 121, 241, 0.15)")
                        property("overflow", "hidden")
                    },
                contentAlignment = Alignment.Center
            ) {
                if (avatarUrl != null) {
                    Div(attrs = {
                        ref { el ->
                            el.innerHTML =
                                "<img src=\"$avatarUrl\" referrerpolicy=\"no-referrer\" " +
                                    "style=\"width:100%;height:100%;object-fit:cover;display:block;\" />"
                            onDispose { }
                        }
                        style {
                            property("width", "100%")
                            property("height", "100%")
                        }
                    })
                } else {
                    SpanText(
                        user.username.take(1).uppercase(),
                        modifier = Modifier
                            .sFontSize(16)
                            .fontWeight(AppTypography.SemiBold)
                            .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    )
                }
            }
        }
    }
}

@Composable
private fun PillButton(label: String, filled: Boolean, onClick: () -> Unit) {
    val sport = LocalSportColors.current
    Box(
        modifier = Modifier
            .sHeight(46)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("position", "relative")
                property("border-radius", s(100))
                property("padding", "${s(13)} ${s(32)}")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                if (filled) {
                    property("background", sport.primary.toRgb().let {
                        "rgb(${it.red}, ${it.green}, ${it.blue})"
                    })
                } else {
                    property("background", AppGradients.InteractiveSecondary)
                }
            }
    ) {
        if (!filled) GradientBorder(100)
        SpanText(
            label,
            modifier = Modifier
                .sFontSize(16)
                .fontWeight(AppTypography.Medium)
                .sLineHeight(18)
                .sLetterSpacing(0.032)
                .color(if (filled) org.jetbrains.compose.web.css.Color(sport.onPrimary.toString()) else com.arena_web.ui.theme.ThemeVars.Text2)
        )
    }
}
